import React from 'react';
import type BaseTreeView from '../../../../classes/pieces/BaseTreeView';
import type { TreeRowActions } from '../../../../classes/pieces/BaseTreeView';
import type { TreeRow } from '../../../../interfaces/pieces/TreeRow';
import useLiquidationTreeviewStore from '../../../../stores/liquidationTreeviewStore';

const { getState } = useLiquidationTreeviewStore;

interface ActionsLignes {
	/** Actions passées au rendu des lignes (modales, icônes d'action). */
	actions: TreeRowActions;
	/** Dépliage ou repliage d'une ligne par rsuite-table (chevron) : ses enfants sont chargés au premier dépliage. */
	handleExpand: (isExpanded: boolean, rowData: TreeRow) => Promise<void>;
}

/**
 * Modifications de l'arbre d'une année depuis ses lignes : chargement des enfants au dépliage, mise à jour,
 * suppression et ajout de lignes. Tout passe par le store : l'état survit à la navigation.
 *
 * @param onDepliage appelé quand l'utilisateur déplie une ligne (message des filtres, useEntitesVideesParFiltre)
 */
const useActionsLignes = (
	anneeExercice: number,
	dataTreeView: BaseTreeView,
	onDepliage?: (row: TreeRow) => void,
): ActionsLignes => {
	/**
	 * Charge les enfants d'une ligne et les enregistre. Un échec (null, déjà signalé) n'est pas retenu : nouvel
	 * essai au prochain dépliage ; une liste vide l'est (bordereau sans pièce, ou vidé par un déplacement).
	 */
	const chargerEnfants = React.useCallback(
		async (row: TreeRow): Promise<void> => {
			getState().setLoading(anneeExercice, row.rowKey, true);
			try {
				const children = await dataTreeView.loadData(row);
				if (children) {
					getState().setEnfants(anneeExercice, row.rowKey, children);
				}
			} finally {
				getState().setLoading(anneeExercice, row.rowKey, false);
			}
		},
		[anneeExercice, dataTreeView],
	);

	const handleExpand = React.useCallback(
		async (isExpanded: boolean, rowData: TreeRow): Promise<void> => {
			if (!rowData?.rowKey) return;
			const { rowKey } = rowData;
			const arbre = getState().annees.get(anneeExercice);
			if (!arbre) return;
			if (isExpanded) {
				onDepliage?.(rowData);
			}

			if (isExpanded && !arbre.index.hasLoadedChildren(rowKey)) {
				if (arbre.loadingKeys.has(rowKey)) return;
				await chargerEnfants(arbre.index.get(rowKey) ?? rowData);
			}
			getState().setExpanded(anneeExercice, rowKey, isExpanded);
		},
		[anneeExercice, chargerEnfants, onDepliage],
	);

	const actions = React.useMemo<TreeRowActions>(
		() => ({
			changeRow: async (row, shouldRefreshChildren) => {
				getState().updateIndex(anneeExercice, (index) =>
					index.updateNode(row.rowKey, () => row),
				);
				const { index } = getState().annees.get(anneeExercice) ?? {};
				// des enfants non chargés le seront, à jour, au dépliage
				if (shouldRefreshChildren && index?.hasLoadedChildren(row.rowKey)) {
					await chargerEnfants(index.get(row.rowKey));
				}
			},
			deleteRow: (rowKey) =>
				getState().updateIndex(anneeExercice, (index) =>
					index.deleteNode(rowKey),
				),
			addRow: (parentKey, row) =>
				getState().updateIndex(anneeExercice, (index) =>
					index.addChild(
						parentKey,
						row,
						dataTreeView.compareChildren(index.get(parentKey)?.nodeType),
					),
				),
		}),
		[anneeExercice, chargerEnfants, dataTreeView],
	);

	return { actions, handleExpand };
};

export default useActionsLignes;
